import { cancelAnimations, playAnimation, settleAnimations } from '../../components/motion-runner.js';
import { canAnimateElement, readCssTimeMs } from '../../components/motion-policy.js';
import { playDetailsHeightMotion } from '../../components/details-motion.js';

export function initRankingCreateDisclosure(
    documentRef = globalThis.document,
    windowRef = globalThis.window,
) {
    const disclosure = documentRef?.querySelector?.('[data-rankings-create]');
    if (!(disclosure instanceof HTMLDetailsElement) || disclosure.dataset.rankingsCreateBound === 'true') {
        return;
    }

    const trigger = disclosure.querySelector('.rankings-create-trigger');
    const content = disclosure.querySelector('.rankings-create-content');
    if (!(trigger instanceof HTMLElement) || !(content instanceof HTMLElement)) {
        return;
    }

    disclosure.dataset.rankingsCreateBound = 'true';
    trigger.setAttribute('aria-expanded', disclosure.open ? 'true' : 'false');
    let isAnimating = false;
    let isTypeAnimating = false;

    const applyExpandedState = (expanded) => {
        disclosure.open = expanded;
        content.inert = !expanded;
        trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    };

    const resetMotionStyles = () => {
        disclosure.style.removeProperty('height');
        disclosure.style.removeProperty('overflow');
    };

    const transitionExpandedState = async (expanded) => {
        if (isAnimating || isTypeAnimating || disclosure.open === expanded) {
            return;
        }

        if (!canAnimateElement(disclosure, windowRef)) {
            applyExpandedState(expanded);
            return;
        }

        isAnimating = true;
        const startHeight = disclosure.getBoundingClientRect().height;
        if (expanded) {
            applyExpandedState(true);
            content.inert = true;
        } else {
            content.inert = true;
            trigger.setAttribute('aria-expanded', 'false');
        }

        const endHeight = expanded ? disclosure.getBoundingClientRect().height : 0;
        await playDetailsHeightMotion(disclosure, content, {
            startHeight,
            endHeight,
            expanded,
            duration: readCssTimeMs(
                disclosure,
                expanded ? '--rankings-create-open-duration' : '--rankings-create-close-duration',
                expanded ? 320 : 260,
            ),
        });

        if (!expanded) {
            applyExpandedState(false);
        }
        resetMotionStyles();
        content.inert = !expanded;
        isAnimating = false;
    };

    const fields = disclosure.querySelector('.rankings-create-fields');
    const positionsField = disclosure.querySelector('.rankings-positions-field');
    const typeInputs = Array.from(disclosure.querySelectorAll('input[name="type"]'))
        .filter((input) => input instanceof HTMLInputElement);
    const slottedInput = typeInputs.find((input) => input.value === 'slotted');

    if (
        fields instanceof HTMLElement
        && positionsField instanceof HTMLElement
        && slottedInput instanceof HTMLInputElement
    ) {
        fields.classList.add('is-type-motion-enhanced');
        positionsField.hidden = slottedInput.checked;
        positionsField.inert = slottedInput.checked;

        const transitionPositionsField = async (showPositions) => {
            if (isAnimating || isTypeAnimating || positionsField.hidden === !showPositions) {
                return;
            }

            if (!canAnimateElement(disclosure, windowRef)) {
                positionsField.hidden = !showPositions;
                positionsField.inert = !showPositions;
                return;
            }

            // Nothing here is made inert or disabled: the radio that fired this is the focused element,
            // and taking focus off it mid-animation drops a keyboard user back at the top of the page.
            // A switch arriving during the animation is dropped by the guard above and caught up below.
            isTypeAnimating = true;

            const startContainerHeight = disclosure.getBoundingClientRect().height;
            const startFieldHeight = showPositions ? 0 : positionsField.getBoundingClientRect().height;
            const naturalFieldMargin = Number.parseFloat(
                windowRef?.getComputedStyle?.(positionsField)?.marginTop ?? '',
            ) || 14;
            const startFieldMargin = showPositions ? 0 : naturalFieldMargin;
            positionsField.hidden = !showPositions;
            const endContainerHeight = disclosure.getBoundingClientRect().height;
            const endFieldHeight = showPositions ? positionsField.getBoundingClientRect().height : 0;
            const endFieldMargin = showPositions ? naturalFieldMargin : 0;
            if (!showPositions) {
                positionsField.hidden = false;
            }

            disclosure.style.setProperty('height', `${startContainerHeight}px`);
            disclosure.style.setProperty('overflow', 'hidden');
            positionsField.style.setProperty('height', `${startFieldHeight}px`);
            positionsField.style.setProperty('margin-top', `${startFieldMargin}px`);
            positionsField.style.setProperty('overflow', 'hidden');

            const duration = readCssTimeMs(
                disclosure,
                '--rankings-create-type-duration',
                240,
            );
            const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
            const animations = [
                playAnimation(disclosure, [
                    { height: `${startContainerHeight}px`, overflow: 'hidden' },
                    { height: `${endContainerHeight}px`, overflow: 'hidden' },
                ], { duration, easing, fill: 'forwards' }),
                playAnimation(positionsField, [
                    {
                        height: `${startFieldHeight}px`,
                        marginTop: `${startFieldMargin}px`,
                        opacity: showPositions ? 0 : 1,
                        transform: showPositions ? 'translateY(-6px)' : 'translateY(0)',
                    },
                    {
                        height: `${endFieldHeight}px`,
                        marginTop: `${endFieldMargin}px`,
                        opacity: showPositions ? 1 : 0,
                        transform: showPositions ? 'translateY(0)' : 'translateY(-6px)',
                    },
                ], { duration, easing, fill: 'both' }),
            ].filter(Boolean);

            await settleAnimations(animations);
            if (!showPositions) {
                positionsField.hidden = true;
            }
            cancelAnimations(animations);
            resetMotionStyles();
            positionsField.style.removeProperty('height');
            positionsField.style.removeProperty('margin-top');
            positionsField.style.removeProperty('overflow');
            positionsField.inert = !showPositions;
            isTypeAnimating = false;

            // The kind now selected wins over the one this run animated towards.
            if (slottedInput.checked === showPositions) {
                await transitionPositionsField(!slottedInput.checked);
            }
        };

        typeInputs.forEach((input) => {
            input.addEventListener('change', () => {
                if (!input.checked) {
                    return;
                }

                return transitionPositionsField(input.value === 'ordered');
            });
        });
    }

    content.inert = !disclosure.open;
    disclosure.addEventListener('toggle', () => {
        if (!isAnimating) {
            content.inert = !disclosure.open;
            trigger.setAttribute('aria-expanded', disclosure.open ? 'true' : 'false');
        }
    });

    trigger.addEventListener('click', (event) => {
        event.preventDefault();
        return transitionExpandedState(!disclosure.open);
    });

    // Without JS this is a link back to the index, which reloads the page with the panel closed. With
    // JS it is the close the trigger also is - and focus has to leave the panel before it goes inert.
    const cancel = disclosure.querySelector('[data-rankings-create-cancel]');
    if (cancel instanceof HTMLElement) {
        cancel.addEventListener('click', (event) => {
            event.preventDefault();
            trigger.focus?.();

            return transitionExpandedState(false);
        });
    }

    // The empty state repeats this trigger where the list would have been. It opens the same panel, and
    // focus follows into it - the panel is inert until the animation ends, so only after it.
    const opener = documentRef?.querySelector?.('[data-rankings-create-open]');
    if (opener instanceof HTMLElement) {
        opener.addEventListener('click', async (event) => {
            event.preventDefault();
            await transitionExpandedState(true);
            const firstField = disclosure.querySelector('input[name="name"]');
            if (firstField instanceof HTMLElement) {
                firstField.focus?.();
            }
        });
    }
}
